import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/** Trust-score tiers, in ascending order, with their (integer) display ranges.
 *  Thresholds match the aits-2.0 dashboard mock and the historical gauge's
 *  70/90 boundaries: a 68 reads as "Invisible", 70+ as "Unverified", etc. */
const TIERS = [
  { key: "invisible", label: "Invisible", lo: 0, hi: 69 },
  { key: "unverified", label: "Unverified", lo: 70, hi: 79 },
  { key: "trusted", label: "Trusted", lo: 80, hi: 92 },
  { key: "authority", label: "Authority", lo: 93, hi: 100 },
] as const;

/** Band edges (lower bound of each next tier) partitioning 0–100 with no gap, so
 *  the marker maps continuously even though the display ranges skip a unit. */
const EDGES = [0, 70, 80, 93, 100] as const;
const SEG_PCT = 100 / TIERS.length; // 25% — each tier gets an equal visual quarter

export type ScoreTier = (typeof TIERS)[number]["key"];

/** The tier a score falls in. Band boundaries belong to the HIGHER tier (70 →
 *  Unverified, 93 → Authority), matching the mock's labelling. */
export function tierForScore(score: number): ScoreTier {
  if (score >= 93) return "authority";
  if (score >= 80) return "trusted";
  if (score >= 70) return "unverified";
  return "invisible";
}

const clamp = (n: number) => Math.max(0, Math.min(100, n));

/** Marker position (0–100%) on the evenly-quartered bar: each tier occupies a
 *  quarter and the marker interpolates within its tier's band. This decouples
 *  the visual position from the raw score so the (very unequal) tier ranges each
 *  get an equal, readable label column — the honest ranges still show as text. */
function posForScore(score: number): number {
  const s = clamp(score);
  for (let i = 0; i < TIERS.length; i++) {
    const lo = EDGES[i];
    const hi = EDGES[i + 1];
    if (s < hi || i === TIERS.length - 1) {
      const within = hi > lo ? Math.max(0, Math.min(1, (s - lo) / (hi - lo))) : 0;
      return Math.round((i + within) * SEG_PCT * 100) / 100;
    }
  }
  return 0;
}

type Props = {
  /** 0–100. Placed on the bar at `posForScore(score)%` (piecewise per tier). */
  score: number;
  /** Small uppercase kicker above the headline, e.g. "AI Trust Signal Score". */
  eyebrow?: React.ReactNode;
  /** Plain-English takeaway, e.g. "AI sees you as an unverified source.". */
  headline?: React.ReactNode;
  /** Signed change vs the previous period. `null` → no delta; `0` → "No change". */
  delta?: number | null;
  /** Trailing text after the delta magnitude. */
  deltaSuffix?: string;
  /** Peer benchmark score (0–100). `null` → no peer tick. */
  peer?: number | null;
  /** Peer tick label; defaults to `peers · {peer}`. */
  peerLabel?: string;
  /** Smaller number + headline for side columns / mobile. */
  compact?: boolean;
  /** `false` hides the numeral + delta (free/teaser reports gate the exact
   *  score behind an estimated range — the bar still shows the tier position). */
  showNumber?: boolean;
  className?: string;
};

/** The report trust score on a 4-tier segmented bar (Invisible / Unverified /
 *  Trusted / Authority). Replaces the radial gauge. A dot marks the score, an
 *  optional peer tick floats above, and the big tier-coloured tabular number +
 *  up/down delta sit top-right. Mirrors the OrbitBar shared-bar pattern. */
export function ScoreBar({
  score,
  eyebrow,
  headline,
  delta = null,
  deltaSuffix = "vs last month",
  peer = null,
  peerLabel,
  compact = false,
  showNumber = true,
  className,
}: Props) {
  const s = clamp(score);
  const current = tierForScore(s);
  const dotPos = posForScore(s);

  // Peer tick and score dot collide when their bar positions are within a few
  // percent; drop the tick in that case (the number still carries the score).
  const peerPos = peer != null ? posForScore(clamp(peer)) : null;
  const showPeer = peerPos != null && Math.abs(peerPos - dotPos) > 3;

  const tierLabel = TIERS.find((t) => t.key === current)!.label;
  const frameLabel =
    `Trust score ${s} of 100 — ${tierLabel} tier.` +
    (peer != null ? ` Peers score ${clamp(peer)}.` : "");

  return (
    <div className={cn("vb-scorebar", compact && "vb-scorebar--compact", className)}>
      <div className="vb-scorebar__head">
        {eyebrow != null && <div className="vb-scorebar__eyebrow">{eyebrow}</div>}
        {showNumber && (
          <div className="vb-scorebar__value">
            <span className={cn("vb-scorebar__num", `vb-scorebar__num--${current}`)}>{s}</span>
            <ScoreDelta value={delta} suffix={deltaSuffix} />
          </div>
        )}
        {headline != null && <div className="vb-scorebar__headline">{headline}</div>}
      </div>

      <div
        className={cn("vb-scorebar__frame", showPeer && "vb-scorebar__frame--peer")}
        role="img"
        aria-label={frameLabel}
      >
        {showPeer && (
          <div
            className="vb-scorebar__peer"
            style={{ left: `${peerPos}%` }}
            aria-hidden="true"
          >
            <span className="vb-scorebar__peer-label">{peerLabel ?? `peers · ${clamp(peer!)}`}</span>
            <span className="vb-scorebar__peer-tick" />
          </div>
        )}
        <div className="vb-scorebar__dot" style={{ left: `${dotPos}%` }} aria-hidden="true" />
        <div className="vb-scorebar__bar" aria-hidden="true">
          {TIERS.map((t) => (
            <div key={t.key} className={`vb-scorebar__seg vb-scorebar__seg--${t.key}`} />
          ))}
        </div>
        <div className="vb-scorebar__tiers" aria-hidden="true">
          {TIERS.map((t) => (
            <div
              key={t.key}
              className={cn(
                `vb-scorebar__tier vb-scorebar__tier--${t.key}`,
                t.key === current && "vb-scorebar__tier--current",
              )}
            >
              <span className="vb-scorebar__tier-nm">{t.label}</span>
              <span className="vb-scorebar__tier-rg">
                {t.lo}–{t.hi}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The score's period-over-period change: up = green, down = red, 0 = muted
 *  "No change", null = nothing. Direction is also given as text for SR users
 *  (the chevron is decorative). */
function ScoreDelta({ value, suffix }: { value: number | null; suffix: string }) {
  if (value == null) return null;
  if (value === 0) {
    return <div className="vb-scorebar__delta vb-scorebar__delta--flat">No change</div>;
  }
  const up = value > 0;
  const mag = Math.abs(value);
  return (
    <div
      className={cn("vb-scorebar__delta", up ? "vb-scorebar__delta--up" : undefined)}
      aria-label={`${up ? "up" : "down"} ${mag} ${suffix}`}
    >
      {up ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
      {mag} {suffix}
    </div>
  );
}

export default ScoreBar;
